import { useState } from 'react';
import { apiPost, ApiError } from './api';

// Shared scoring engine for Lead Score AND Contacts (Phase 11). Runs scoped batch
// scoring (all / next-N) with a real X-of-Y progress bar, plus individual per-row
// scoring — all through POST /api/tools/leadfinder/score. `scoring` is exposed so
// callers can disable completion/navigation actions while a run is in flight
// (Phase 10: an early click must never truncate a scoring run).

export interface ScoreScope {
  batch?: string | number;
  tier?: string;
  category?: string;
  researched?: string;
  workStatus?: string;
  stage?: string;
  q?: string;
}

export interface ScoreResult { id: number; score: number; tier: string }

const CHUNK = 25;

export function useScorer(onScored?: (results: ScoreResult[]) => void) {
  const [scoring, setScoring] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Score a scope: mode 'all' drains every unscored-in-scope; a number scores up
  // to that many. Returns the number actually scored.
  async function scoreScope(scope: ScoreScope, mode: 'all' | number): Promise<number> {
    if (scoring) return 0;
    setScoring(true); setMsg(null); setProgress({ done: 0, total: typeof mode === 'number' ? mode : 0 });
    let done = 0;
    let total: number | null = typeof mode === 'number' ? mode : null;
    try {
      for (;;) {
        const want = mode === 'all' ? CHUNK : Math.min(CHUNK, mode - done);
        if (want <= 0) break;
        const r = await apiPost<{ scored: number; remaining: number; results: ScoreResult[] }>(
          '/api/tools/leadfinder/score', { scope, limit: want }
        );
        done += r.scored || 0;
        if (total == null) total = done + (r.remaining || 0);
        else if (mode === 'all') total = Math.max(total, done + (r.remaining || 0));
        setProgress({ done, total });
        onScored?.(r.results || []);
        if (!r.scored || !r.remaining) break;
        if (mode !== 'all' && done >= mode) break;
      }
      setMsg(done ? `Scored ${done}.` : 'Nothing left to score here.');
      return done;
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Scoring failed.');
      return done;
    } finally {
      setProgress(null);
      setScoring(false);
    }
  }

  // Score one contact from its row.
  async function scoreOne(id: number): Promise<ScoreResult | null> {
    if (scoring) return null;
    try {
      const r = await apiPost<{ results: ScoreResult[] }>('/api/tools/leadfinder/score', { contactIds: [id] });
      const one = (r.results || [])[0] || null;
      if (one) onScored?.([one]);
      return one;
    } catch {
      return null;
    }
  }

  return { scoring, progress, msg, setMsg, scoreScope, scoreOne };
}
